// LIBRERIE/IMPORTS
import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

// CONFIGURAZIONI
import { AGENCY_ID } from "../../config/appConfig";
import { getContractTranslation, LanguageCode } from "../../config/i18n/elementsMappingProperty";
import { getTipologiaTranslation } from "../../config/i18n/propertyTranslations";

// HOOKS
import { useGetAgencyDetails } from "../../hooks/useGetAgencyDetails";
import { useGetProvince } from "../../hooks/standard/indirizzi/useGetProvince";
import { useGetComuni } from "../../hooks/standard/indirizzi/useGetComuni";
import { useGetFrazioni } from "../../hooks/standard/indirizzi/useGetFrazioni";
import { useGetContratti } from "../../hooks/standard/useGetContracts";
import { useGetTipologie } from "../../hooks/standard/useGetTipologies";
import { useSendEmailBuy } from "../../hooks/useSendEmail";

// COMPONENTI
import SubHeaderDefault from "../Header/subHeader/SubHeaderDefault";
import LoadingIndicator from "../Loaders/LoadingIndicator";
import NotFound from "../Error/NotFound";
import WidgetPartners from "../Widgets/WidgetPartners/WidgetPartners";
import WidgetContacts from "../Widgets/WidgetContacts/WidgetContacts";

interface Option {
    value: number | string | null;
    label: string | null;
}

const Buy: React.FC = () => {
    const { t, i18n } = useTranslation();
    const [selectedProvince, setSelectedProvince] = useState<Option | null>(null);
    const [comuniOptions, setComuniOptions] = useState<Option[]>([]);
    const [selectedComune, setSelectedComune] = useState<Option | null>(null);
    const [frazioneOptions, setFrazioniOptions] = useState<Option[]>([]);
    const [privacyConsent, setPrivacyConsent] = useState(false);

    const { agency, loading: agencyLoading, error: agencyError } = useGetAgencyDetails(AGENCY_ID);
    const { province } = useGetProvince();
    const { comuni, loading: comuniLoading, error: comuniError } = useGetComuni(selectedProvince?.value ? String(selectedProvince.value) : "");
    const { frazioni, loading: frazioniLoading, error: frazioniError } = useGetFrazioni(selectedComune?.value ? String(selectedComune.value) : "");
    const { contratti } = useGetContratti();
    const { tipologie } = useGetTipologie();

    const { executeRecaptcha } = useGoogleReCaptcha();

    const { sendEmail, isLoading, emailError, success } = useSendEmailBuy();

    useEffect(() => {
        if (!comuniLoading && !comuniError && Array.isArray(comuni)) {
            setComuniOptions(comuni.map(c => ({ value: c.codiceIstatComuneAlfa, label: c.nomeIt })));
        } else {
            setComuniOptions([]);
        }
        setSelectedComune(null);
    }, [comuni, comuniLoading, comuniError, selectedProvince]);

    useEffect(() => {
        if (!frazioniLoading && !frazioniError && Array.isArray(frazioni)) {
            setFrazioniOptions(frazioni.map(f => ({ value: f.id, label: f.frazione })));
        } else {
            setFrazioniOptions([]);
        }
    }, [frazioni, frazioniLoading, frazioniError, selectedComune]);

    const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!privacyConsent) {
            alert(t('validation.privacyAcceptanceRequired') || "Devi accettare la privacy policy per inviare il modulo.");
            return;
        }

        // 1. Ottieni FormData originale per leggerne i valori
        const formData = new FormData(e.currentTarget);
        const rawData = Object.fromEntries(formData.entries());

        const privacyConsentString = privacyConsent.toString();

        formData.append('buy_privacy', privacyConsentString);
        if (!formData.has('buy_privacy') || formData.get('buy_privacy') === '') {
            formData.append('buy_privacy', privacyConsentString);
        } else {
            formData.set('buy_privacy', privacyConsentString);
        }

        const convertedData: { [key: string]: any } = Object.fromEntries(
            Object.entries(rawData).map(([key, value]) => {
                try {

                    const stringValue = String(value);

                    if (key === 'buy_contratto') {
                        const contractId = Number(stringValue);
                        const translatedLabel = isNaN(contractId) ? null : getContractTranslation(contractId, i18n.language as LanguageCode);
                        return [key, translatedLabel || stringValue || '-'];
                    } else if (key === 'buy_tipologia') {
                        const tipologiaId = Number(stringValue);
                        const translatedLabel = isNaN(tipologiaId) ? null : getTipologiaTranslation(tipologiaId, i18n.language as LanguageCode);
                        return [key, translatedLabel || stringValue || '-'];
                    } else if (key === 'buy_provincia') {
                        const foundProvince = Array.isArray(province) ? province.find(p => p.sigla === stringValue) : undefined;
                        return [key, foundProvince ? `${foundProvince.nome} (${foundProvince.sigla})` : stringValue || '-'];
                    } else if (key === 'buy_comune') {
                        const foundComune = Array.isArray(comuni) ? comuni.find(c => c.codiceIstatComuneAlfa === stringValue) : undefined;
                        return [key, foundComune?.nomeIt || stringValue || '-'];
                    } else if (key === 'buy_frazione') {
                        const frazioneId = Number(stringValue);
                        const foundFrazione = Array.isArray(frazioni) ? frazioni.find(f => f.id === frazioneId || String(f.id) === stringValue || f.frazione === stringValue) : undefined;
                        return [key, foundFrazione?.frazione || stringValue || '-'];
                    } else if (stringValue === '') {
                        return [key, '-'];
                    } else {
                        return [key, stringValue];
                    }
                } catch (conversionError) {
                    console.error(`Errore durante la conversione del campo ${key}:`, conversionError);
                    return [key, value];
                }
            })
        );
        console.log("handleSubmit (Buy): Dati convertiti (con label):", convertedData);

        // 2. Esegui ReCAPTCHA
        if (!executeRecaptcha) {
            console.error("handleSubmit (Buy): executeRecaptcha non disponibile");
            alert(t('validation.internalErrorRecaptcha') || "Errore interno: impossibile verificare il modulo.");
            return;
        }

        let recaptchaToken: string | null = null;

        try {
            recaptchaToken = await executeRecaptcha('submitBuyForm');
            if (!recaptchaToken) {
                console.warn("handleSubmit (Buy): Token reCAPTCHA non ottenuto");
                alert(t('validation.recaptchaTokenError') || "Errore: impossibile verificare che sei umano. Riprova.");
                return;
            }
            console.log("handleSubmit (Buy): Token ReCaptcha ottenuto.");
        } catch (error) {
            console.error("handleSubmit (Buy): Errore durante l'esecuzione di reCAPTCHA:", error);
            alert(t('validation.recaptchaExecutionError') || "Si è verificato un errore durante la verifica reCAPTCHA.");
            return;
        }



        // 3. Chiama funzione sendEmail()
        sendEmail({
            data: convertedData,
            recaptchaToken: recaptchaToken
        });

        // 4. Logica Iubenda (dopo aver INIZIATO la chiamata a sendEmail)
        console.log("Tentativo invio consenso Iubenda...");
        // Inizializza la Consent Solution di Iubenda
        if (window._iub && window._iub.cons_instructions) {
            console.log("window._iub trovato. Invio comando submit.");
            const formElement = document.getElementById("find_form");
            const emailFieldName = "buy_email";
            const nameFieldName = "buy_sender";
            const privacyCheckboxName = "generic";
            console.log("Form Selettore:", formElement);
            console.log("Mapping Nomi:", nameFieldName, emailFieldName, privacyCheckboxName);
            if (formElement) {    
                window._iub.cons_instructions.push(["submit", {
                    form: {
                        selector: formElement,
                        map: {
                            subject: { full_name: nameFieldName, email: emailFieldName },
                            preferences: { privacy_policy: "generic" }
                        },
                        consent: {
                            legal_notices: [{ identifier: privacyCheckboxName }]
                        }
                    }
                }]);
            console.log("Comando submit inviato a Iubenda.");
            } else {
                console.error("Elemento form #buy_form non trovato!");
            }
        } else {
            console.error("ERRORE: window._iub o window._iub.cons_instructions non disponibili!");
        };
    }, [t, privacyConsent, executeRecaptcha, sendEmail, province, comuni, frazioni, i18n.language]);

    const handlePrivacyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPrivacyConsent(e.target.checked);
    };

    // Helper per prevenire lo scroll su focus dei Select
    const preventScrollOnFocus = ( e: React.FocusEvent<HTMLInputElement> | React.FocusEvent<HTMLDivElement> ) => {
        e.preventDefault();
        e.stopPropagation();
    };

    if (agencyLoading) {
        return <LoadingIndicator />;
    }

    if (agencyError) {
        console.error("Errore caricamento dati iniziali per Buy:", agencyError);
        return <NotFound message={t('validation.loadingError', "Errore durante il caricamento dei dati necessari per il modulo.")} />;
    }

    return (
        <>
            <Helmet>
                <title>{t('buyRequestForm.pageTitle')} | {agency?.agencyName ?? t('common.defaultAgencyName')}</title>
            </Helmet>
            <SubHeaderDefault title={t('buyRequestForm.pageTitle')} isSeo={false}/>
            <section className="space-ptb">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-8">
                            <div className="row">
                                <div className="col-lg-12">

                                    <div className="section-title mb-5">
                                        <h3 className="asH2SubTitle">{t('buyRequestForm.formTitle')}</h3>
                                        <span className="lead">{t('buyRequestForm.formSubtitle')}</span>
                                    </div>

                                    <div id="lead-sell-message" style={{ marginBottom: '1rem' }}>
                                        {success && (
                                            <div className="alert alert-success" role="alert">
                                                {t('statusMessages.messageSentSuccessfully') || "Richiesta inviata con successo!"}
                                            </div>
                                        )}
                                        {emailError && (
                                            <div className="alert alert-danger" role="alert">
                                                {typeof emailError === 'string' ? emailError : t('validation.genericRequestError', 'Si è verificato un errore.')}
                                            </div>
                                        )}
                                    </div>

                                    <form
                                        id="find_form"
                                        className=""
                                        onSubmit={handleSubmit}
                                        name="lead_buy_form"
                                        acceptCharset="utf-8"
                                    >
                                        <div className="form-row">

                                        <h6 className="col-12 mb-3">{t('buyRequestForm.personalInfoSectionTitle')}</h6>

                                            <div className="form-group col-md-12">
                                                <input
                                                    type="text"
                                                    id="buy_sender"
                                                    name="buy_sender"
                                                    className="form-control"
                                                    placeholder={t('buyRequestForm.nameSurnamePlaceholder') || "Nome e Cognome"}
                                                    required
                                                />
                                            </div>

                                            <div className="form-group col-md-6">
                                                <input
                                                    type="email"
                                                    id="buy_email"
                                                    name="buy_email"
                                                    className="form-control"
                                                    placeholder={t('buyRequestForm.emailPlaceholder') || "Email"}
                                                    required
                                                    pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
                                                    title={t('validation.emailPatternTitle') || "Inserisci un indirizzo email valido, es. esempio@dominio.com"}
                                                />
                                            </div>

                                            <div className="form-group col-md-6">
                                                <input
                                                    type="text"
                                                    id="buy_tel"
                                                    name="buy_tel"
                                                    className="form-control"
                                                    placeholder={t('buyRequestForm.phonePlaceholder') || "Telefono"}
                                                    required
                                                    pattern="^\+?(\d[\d-. ]+)?(\([\d-. ]+\))?[\d-. ]+\d$"
                                                    title={t('validation.phonePatternTitle') || "Inserisci un numero di telefono corretto, es. +39 345 678 9999"}
                                                />
                                            </div>

                                            <div className="form-group col-md-12">
                                                <textarea
                                                    id="input_message"
                                                    name="buy_message"
                                                    cols={50} rows={5}
                                                    className="form-control"
                                                    placeholder={t('buyRequestForm.messagePlaceholder') || "Messaggio..."}
                                                ></textarea>
                                            </div>

                                            <hr className="col-12 mt-3 mb-4"></hr>

                                            <h6 className="col-12 mb-3">{t('buyRequestForm.propertyInfoSectionTitle')}</h6>

                                            {/* Contratto */}
                                            <div className="form-group col-md-6 select-border">
                                                <Select<Option, false>
                                                    className="form-control basic-select"
                                                    id="input_contratto"
                                                    placeholder={t('buyRequestForm.contractPlaceholder') || "Contratto"}
                                                    required
                                                    name="buy_contratto"
                                                    options={Array.isArray(contratti) ? contratti.map((c) => ({ value: c.id, label: getContractTranslation(c.id, i18n.language as LanguageCode) })) : []}
                                                    menuPortalTarget={document.body}
                                                    styles={{
                                                        container: (provided) => ({ ...provided, width: "100%", backgroundColor: "rgb(242, 242, 242)", border: "none" }),
                                                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                                                    }}
                                                    onFocus={preventScrollOnFocus}
                                                />
                                            </div>

                                            {/* Tipologia */}
                                            <div className="form-group col-md-6 select-border">
                                                <Select<Option, false> 
                                                    className="form-control basic-select"
                                                    id="input_tipologia"
                                                    placeholder={t('buyRequestForm.typePlaceholder') || "Tipologia"}
                                                    required
                                                    name="buy_tipologia"
                                                    options={Array.isArray(tipologie) ? tipologie.map((t) => ({ value: t.id, label: getTipologiaTranslation(t.id, i18n.language as LanguageCode) })) : []}
                                                    menuPortalTarget={document.body}
                                                    styles={{
                                                        container: (provided) => ({ ...provided, width: "100%", backgroundColor: "rgb(242, 242, 242)", border: "none" }),
                                                        menuPortal: base => ({ ...base, zIndex: 9999 })
                                                    }}
                                                    onFocus={preventScrollOnFocus}
                                                />
                                            </div>

                                            {/* Provincia */}
                                            <div className="form-group col-md-6 select-border">
                                                <Select<Option, false> 
                                                    className="form-control basic-select"
                                                    id="input_provincia"
                                                    placeholder={t('buyRequestForm.provincePlaceholder') || "Provincia"}
                                                    required
                                                    name="buy_provincia"
                                                    options={Array.isArray(province) ? province.map((p) => ({ value: p.sigla, label: p.nome })) : []}
                                                    onChange={setSelectedProvince}
                                                    menuPortalTarget={document.body}
                                                    styles={{
                                                        container: (provided) => ({ ...provided, width: "100%", backgroundColor: "rgb(242, 242, 242)", border: "none" }),
                                                        menuPortal: base => ({ ...base, zIndex: 9999 })
                                                    }}
                                                    onFocus={preventScrollOnFocus}
                                                />
                                            </div>

                                            {/* Comune */}
                                            <div className="form-group col-md-6 select-border">
                                                <Select<Option, false> 
                                                    className="form-control basic-select"
                                                    id="input_comune"
                                                    placeholder={t('buyRequestForm.cityPlaceholder') || "Comune"}
                                                    required
                                                    name="buy_comune"
                                                    options={comuniOptions}
                                                    onChange={setSelectedComune}
                                                    isDisabled={!selectedProvince}
                                                    menuPortalTarget={document.body}
                                                    styles={{
                                                        container: (provided) => ({ ...provided, width: "100%", backgroundColor: "rgb(242, 242, 242)", border: "none" }),
                                                        menuPortal: base => ({ ...base, zIndex: 9999 })
                                                    }}
                                                    onFocus={preventScrollOnFocus}
                                                    noOptionsMessage={() => !selectedProvince ? t('searchWidget.selectProvinceFirst') : t('common.noResults')}
                                                />
                                            </div>

                                            {/* Frazione */}
                                            <div className="form-group col-md-6 select-border">
                                                <Select<Option, false> 
                                                    className="form-control basic-select"
                                                    id="input_frazione"
                                                    placeholder={t('buyRequestForm.fractionPlaceholder') || "Frazione"}
                                                    name="buy_frazione"
                                                    options={frazioneOptions}
                                                    isDisabled={!selectedComune}
                                                    menuPortalTarget={document.body}
                                                    styles={{
                                                        container: (provided) => ({ ...provided, width: "100%", backgroundColor: "rgb(242, 242, 242)", border: "none" }),
                                                        menuPortal: base => ({ ...base, zIndex: 9999 })
                                                    }}
                                                    onFocus={preventScrollOnFocus}
                                                    noOptionsMessage={() => !selectedComune ? t('searchWidget.selectCityFirst') : t('common.noResults')}
                                                />
                                            </div>

                                            {/* Superficie */}
                                            <div className="form-group col-md-6 select-border">
                                                <input
                                                    type="number"
                                                    id="buy_mq"
                                                    name="buy_mq"
                                                    className="form-control"
                                                    placeholder={t('buyRequestForm.surfacePlaceholder') || "Superficie (mq)"}
                                                    min="0"
                                                    step="10"
                                                /*  style={{
                                                        alignItems: "center",
                                                        display: "flex",
                                                        flexWrap: "wrap",
                                                        boxSizing: "border-box",
                                                        borderWidth: "1px",
                                                        borderStyle: "solid",
                                                        borderRadius: "5px",
                                                        borderColor: "rgba(230, 230, 230)",
                                                        position: "relative",
                                                        outline: "0px !important",
                                                        height: "38px",
                                                        width: "304px",
                                                        justifyContent: "space-between",
                                                    }}*/
                                                />
                                            </div>

                                            <div className="form-group col-md-12">
                                                <div className="custom-control custom-checkbox">
                                                    <input
                                                        type="checkbox"
                                                        id="buy_privacy"
                                                        name="generic"
                                                        value="1"
                                                        className="custom-control-input"
                                                        required
                                                        checked={privacyConsent}
                                                        onChange={handlePrivacyChange}
                                                    />
                                                    <label className="custom-control-label" htmlFor="buy_privacy">
                                                        {t('buyRequestForm.privacyConsentText') || "Accetto l'"}
                                                        <Link to="/privacy" title={t('privacyPolicy.title') || "Privacy Policy"}>
                                                            {t('buyRequestForm.privacyPolicyLinkText') || "informativa sulla Privacy"}
                                                        </Link>
                                                    </label>
                                                </div>
                                            </div>

                                            <div className="col-md-12">
                                                <button
                                                    id="contact-submit"
                                                    type="submit"
                                                    className="btn btn-primary"
                                                    disabled={isLoading}
                                                >
                                                    {isLoading ? (t('buyRequestForm.sendingButton') || 'Invio...') : (t('buyRequestForm.submitButton') || 'Invia Richiesta')}                                                </button>
                                            </div>
                                        </div>
                                    </form>

                                </div>

                            </div>

                        </div>
                        <div className="col-lg-4 mt-4 mt-lg-0">
                            <div className="sidebar">
                                <WidgetContacts
                                    agencyDataAddress={agency?.address_details?.address ?? ''}
                                    agencyDataCity={agency?.address_details?.city_name ?? ''}
                                    agencyDataComuneCapoluogo={agency?.address_details?.city_name === agency?.address_details?.province_name}
                                    agencyDataProvinciaSigla={agency?.address_details?.sigla ?? ''}
                                    agencyDataEmail={''}
                                    agencyDataEmailShow={false}
                                    agencyDataPhone1={agency?.phone1 ?? ''}
                                    agencyDataPhone2={agency?.phone2 ?? ''}
                                    agencyDataInternationalPrefix={'+39'}
                                    agencyDataMobile={agency?.mobile ?? ''}
                                    agencyDataMobile2={agency?.mobile2 ?? ''}
                                    agencyDataFax={agency?.fax ?? ''}
                                    agencyDataWhatsapp={''}
                                    agencyDataFacebook={''}
                                    agencyDataInstagram={''}
                                    agencyDataTwitter={''}
                                    agencyDataLinkedin={''}
                                    agencyDataPinterest={''}
                                />
                                <WidgetPartners
                                    partners={[
                                        {
                                            url: agency?.id ? `http://www.casain24ore.it/fromsito.php?agReg=${agency.id}` : 'http://www.casain24ore.it',
                                            data: { imgSrc: '/assets/images/c24.png', imgAlt: t('widgets.partners.c24NetworkAlt') || 'Network Immobiliare' }
                                        }
                                    ]}
                                />
                            </div>
                        </div>
                    </div>

                </div>
            </section>
        </>
    );
};

export default Buy;